<template>
  <v-container :class="$abem({block: 'o-ovulationCalculator', modifiers: [{ loaded: this.loaded }]})">
    <v-row>
      <v-col cols="12" sm="6" :class="$abem({ block: 'o-ovulationCalculator', element: 'column' })">
        <div :class="$abem({ element: 'step', modifiers: ['active'] })">
          <Title :prefix="1" :text="$t('What is your usual cycle length?')" />
          <v-select
            :class="$abem({ element: 'cycleLength' })"
            v-model="selectedCycleLength"
            :items="cycleLengths"
            item-text="text"
            item-value="value"
            :label="$t('Cycle Length')"
            persistent-hint
            single-line
            @change="step = 2; resetFertileWindowDates()"
          ></v-select>

          <div :class="$abem({ element: 'dontKnow' })">
            <a href="#"  @click="toggleShowDontKnowHelp($event)" class="primary--text">{{ $t("Don't know") }}</a>
            <p :class="$abem({ element: 'dontKnowText', modifiers: [{ collapsed: !showDontKnowHelp }] })">
              {{ $t("In order to use this tool you will need to keep track of the dates of your recent menstrual periods") }}.
              {{ $t("Your cycle length is the number of days between the first day of bleeding of one period, and the first day of bleeding of your next period") }}.
              {{ $t("This tends to vary from 23 to 35 days with the average being a 28 day cycle") }}.
              {{ $t("If you have irregular menstrual cycles, or your cycle length varies from month to month, it may be difficult to calculate your ovulation date") }}.
              {{ $t("In this case, use your shortest period date") }}.
              {{ $t("It is important to start earlier to ensure your ovulation date is not missed") }}.
              {{ $t("Ovulation urine tests may be useful, and you should consider seeking further advice from your GP or fertility specialist") }}.
            </p>
          </div>

        </div>

        <div :class="$abem({ element: 'step', modifiers: [{ hidden: step < 2}, { active: step >= 2}] })">
          <Title :prefix="2" :text="$t('When was the first day of your last period?')" />
          <v-date-picker
            :class="$abem({ element: 'datePicker' })"
            v-model="lastPeriodIso"
            :events="calculatedDays"
            :locale="$getLanguage()"
            :allowedDates="allowedDates"
            show-current="false"
            no-title
            @change="step = 3; resetFertileWindowDates()"
          ></v-date-picker>

          <dl :class="$abem({ element: 'calendarKey', modifiers: [{ hidden: this.step < 3 }, { visible: this.step >= 3 }] })">
            <dt><span :style="`background-color: ${colors.period}`"></span></dt>
            <dd>{{ $t("Period") }}</dd>
            <dt><span :style="`background-color: ${colors.fertile}`"></span></dt>
            <dd>{{ $t("Fertile Window") }}</dd>
            <dt><span :style="`background-color: ${colors.ovulation}`"></span></dt>
            <dd>{{ $t("Likely Ovulation Day") }}</dd>
          </dl>
        </div>

      </v-col>

      <v-col cols="12" sm="6" :class="$abem({ element: 'column' })">

        <div :class="$abem({ element: 'step', modifiers: [{ hidden: step < 3}, { active: step >= 3}] })">
          <Title :prefix="3" :text="$t('Your fertile windows')" />
          <div :class="$abem({ element: 'fertileWindows' })">
            <p>
              {{ $t("Your next estimated fertile window will be") }}:
            </p>
            <p>
              <strong class="primary--text">{{ firstFertileWindowText }}</strong>
            </p>
            <p>
              {{ $t("if you continue to have regular cycles") }}
            </p>
            <p class="mt-3">
              {{ $t("Your fertile window after that will be") }}:
            </p>
            <p>
              <strong class="primary--text">{{ secondFertileWindowText }}</strong>
            </p>
          </div>
          <p :class="$abem({ element: 'note' })">
            <strong>{{ $t("Please note") }}:</strong> {{ $t("If the first day of your period or your cycle length changes, you will need to recalculate") }}
          </p>
        </div>

        <div v-if="isReminderVariant" :class="$abem({ element: 'step', modifiers: [{ hidden: step < 3}, { active: step >= 3}] })">
          <ReminderForm
            v-show="step >= 3"
            :step="step"
            :lastPeriodDate="lastPeriodIso"
            :cycleLength="selectedCycleLength"
            :fertileWindowDates="sortedFertileWindowDates"
            :urlParameters="urlParameters"
          />
        </div>

        <div v-if="!isReminderVariant" :class="$abem({ element: 'step', modifiers: [{ hidden: step < 3}, { active: step >= 3}] })">
          <p :class="$abem({ element: 'cta' })">
            <a href="#" @click="redirectContactPage()" class="primary">{{ $t("Request an appointment") }}
            <svg class="a-icon">
              <use xlink:href="/base-theme/dist/img/sprite/sprite.svg?1565799165#arrow-right"></use>
            </svg>
            </a>
          </p>
        </div>

      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { addDays, addMonths, differenceInDays, format, isAfter } from 'date-fns'

import Title from './Title'
import ReminderForm from './ReminderForm'

function parseLocationQueryString () {
  return window.location.search.substring(1).split('&').reduce((acc, keyValuePair) => {
    const [key, value] = keyValuePair.split('=')
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(value)
    return acc
  }, {})
}

export default {
  name: 'Calculator',
  components: {
    Title,
    ReminderForm
  },
  mounted () {
    this.urlParameters = parseLocationQueryString()

    setTimeout(() => {
      // Softer inital render - better UX.
      this.loaded = true
    }, 1000)
  },
  updated () {
    // Trigger attachBehaviors() in parent Drupal window if inside an iframe
    // when data is updated in Vue. See reflowIframe plugin.
    this.$reflowIframe()
  },
  methods: {
    /**
     * Define which dates are allowed to be selected on the date calendar.
     */
    allowedDates (date) {
      const distanceFromNow = differenceInDays(new Date(date), this.now)
      // Allow any date from 60 days ago up until now.
      return distanceFromNow <= 0 && distanceFromNow > -60
    },

    toggleShowDontKnowHelp (e) {
      this.showDontKnowHelp = !this.showDontKnowHelp
      e.preventDefault()
      return false
    },

    /**
     * Callback for calendar events. This function is called for each and every
     * one of the visible calendar dates.
     */
    calculatedDays (date, recursionDepth = 0) {
      if (this.step < 3) {
        return
      }
      const differenceDaysFirstOvulation = differenceInDays(new Date(date), this.firstOvulationDate)
      const differenceDaysSecondOvulation = differenceInDays(new Date(date), this.secondOvulationDate)

      const differenceDaysFirstPeriod = differenceInDays(new Date(date), this.firstPeriodDateStart)
      const differenceDaysSecondPeriod = differenceInDays(new Date(date), this.secondPeriodDateStart)

      let isPeriodWindowDate = (differenceDaysFirstPeriod >= 0 && differenceDaysFirstPeriod < 5) || (differenceDaysSecondPeriod >= 0 && differenceDaysSecondPeriod < 5) ? true : false
      let isOvulationDate = (differenceDaysFirstOvulation === 0) || differenceDaysSecondOvulation === 0 ? true : false

      let isFertileWindowDateMonth1 = (differenceDaysFirstOvulation > -7 && differenceDaysFirstOvulation < 0) ? true : false
      let isFertileWindowDateMonth2 = (differenceDaysSecondOvulation > -7 && differenceDaysSecondOvulation < 0) ? true : false
      let isFertileWindowDate = isFertileWindowDateMonth1 || isFertileWindowDateMonth2

      isFertileWindowDateMonth1 && !this.fertileWindowDates.month1.includes(date) && this.fertileWindowDates.month1.push(date)
      isFertileWindowDateMonth2 && !this.fertileWindowDates.month2.includes(date) && this.fertileWindowDates.month2.push(date)

      if (recursionDepth < 2) {
        // Force two next calendar months date calculation so that
        // fertileWindowDates.month2 is ensured to be populated
        // with all available upcoming dates.
        let [year, month, day] = date.split('-')
        const nextMonthDate = addMonths(new Date(year, month -1, day), 1).toISOString().substr(0, 10)
        this.calculatedDays(nextMonthDate, recursionDepth + 1)
      }

      if (recursionDepth === 0) {
        // Colors only need to be returned for the first call to this function.
        // Recursive calls are purely to populate this.fertileWindowDates.
        const colors = []
        isOvulationDate && colors.push(this.colors.ovulation)
        isFertileWindowDate && colors.push(this.colors.fertile)
        isPeriodWindowDate && colors.push(this.colors.period)
        return colors
      }
    },
    resetFertileWindowDates () {
      this.fertileWindowDates = {
        month1: [],
        month2: [],
      }
    },
    _generateFertileWindowText (monthIndex) {
      if (!this.sortedFertileWindowDates[monthIndex]) {
        return ''
      }

      const sortedFertileWindowDates = this.sortedFertileWindowDates[monthIndex]

      const [startYear, startMonth, startDay] = sortedFertileWindowDates[0].split('-')
      const firstDayMonth = format(new Date(startYear, startMonth -1, startDay), 'd-MMMM', { awareOfUnicodeTokens: true }).split('-')

      const lastIndex = sortedFertileWindowDates.length -1
      const [lastYear, lastMonth, lastDay] = sortedFertileWindowDates[lastIndex].split('-')
      const lastDayMonth = format(new Date(lastYear, lastMonth -1, lastDay), 'd-MMMM', { awareOfUnicodeTokens: true }).split('-')

      return `${firstDayMonth[0]} ${this.$t(firstDayMonth[1])} – ${lastDayMonth[0]} ${this.$t(lastDayMonth[1])}`
    },

    redirectContactPage () {
      window.parent.location.href = '/contact'
      return false
    }
  },
  computed: {
    lastPeriodDate () {
      return new Date(this.lastPeriodIso)
    },
    now () {
      return new Date()
    },
    todayIso () {
      return this.now.toISOString().substr(0, 10)
    },
    showFirstUpcomingOvulationDate () {
      // Only show first calculated ovulation date related fertile window if it
      // falls after today. -13 ensures that if the ovulation date is today then
      // it will be included in the first upcoming ovulation date.
      return isAfter(addDays(this.lastPeriodDate, this.selectedCycleLength - 13), this.now)
    },
    firstPeriodDateStart () {
      // If first calculated ovulation date falls today or after then display
      // the first period start date as the nearest upcoming range, otherwise
      // calculate forward 1 cycle length.
      const monthIndex = this.showFirstUpcomingOvulationDate ? 0 : 1
      return addDays(this.lastPeriodDate, (this.selectedCycleLength * monthIndex))
    },
    secondPeriodDateStart () {
      // If first calculated ovulation date falls today or after then display
      // the second period start date as 1 cycle length after the nearest
      // upcoming range, otherwise calculate forward 2 cycle lengths.
      const monthIndex = this.showFirstUpcomingOvulationDate ? 1 : 2
      return addDays(this.lastPeriodDate, (this.selectedCycleLength * monthIndex))
    },
    firstOvulationDate () {
      // The ovulation date is estimated to be 14 days before the first period
      // start date.
      return addDays(this.firstPeriodDateStart, this.selectedCycleLength - 14)
    },
    secondOvulationDate () {
      // The ovulation date is estimated to be 14 days before the second period
      // start date.
      return addDays(this.secondPeriodDateStart, this.selectedCycleLength - 14)
    },
    cycleLengths () {
      const days = this.$t('days')
      return [...Array(17)].map((_, d) => {
        d = d + 20
        return {
          text: `${d} ${days}`, // 20 days .. 36 days
          value: d // 20 .. 36
        }
      })
    },
    sortedFertileWindowDates () {
      if (this.fertileWindowDates['month1'].length === 0) {
        return {}
      }

      return Object.keys(this.fertileWindowDates).reduce((acc, month) => {
        const clonedFertileWindowDates = this.fertileWindowDates[month].slice(0)
        clonedFertileWindowDates.sort(function(a, b) {
          return Date.parse(a) - Date.parse(b)
        })
        acc[month] = clonedFertileWindowDates
        return acc
      }, {})
    },
    firstFertileWindowText () {
      return this._generateFertileWindowText('month1')
    },
    secondFertileWindowText () {
      return this._generateFertileWindowText('month2')
    },
    isReminderVariant () {
      return this.urlParameters.variant && this.urlParameters.variant.includes('reminder')
    },
  },
  data() {
    return {
      urlParameters: {}, // Parameters passed from the url query string - see mounted().
      // Start invisible for fade in effect.
      loaded: false,
      lastPeriodIso: new Date().toISOString().substr(0, 10),
      selectedCycleLength: 0,
      step: 1,
      showDontKnowHelp: false,
      cycleHint: `<a href="#">${this.$t("Don't know")}</a>`,
      fertileWindowDates: {
        month1: [],
        month2: [],
      },
      colors: {
        ovulation: '#6b257d',
        fertile: '#9abf4c',
        period: '#aaaaaa',
      },
    }
  }
}
</script>

<style lang="scss">

@mixin visible-states {
  transition: all 1s;
  &.-hidden {
    display: none;
  }
  &.-active {
    display: block;
  }
}

.o-ovulationCalculator {
  opacity: 0;
  padding: 0;
  transition: all 1s;
  display: flex;
  min-height: 425px;

  &.-loaded {
    opacity: 1;
  }

  &__step {
    @include visible-states;
  }

  &__cycleLength {
    padding: 0;
    margin: 0;
    width: 70%;
    margin: 0 auto;
  }

  &__dontKnow {
    text-align: center;
  }

  &__dontKnowText {
    overflow: hidden;
    max-height: auto;
    transition: all 0.25s ease;
    transform: scaleY(1);
    transform-origin: top;
    margin: 25px;
    font-size: 0.9em;
    text-align: justify;

    &.-collapsed {
      max-height: 0;
      transform: scaleY(0);
    }
  }

  &__datePicker {
    display: flex;
    flex-flow: row wrap;
    flex-grow: 0;
    text-align: left;
    margin: 15px auto;
    max-width: 300px;
  }

  &__calendarKey {
    @include visible-states;

    display: flex;
    flex-flow: row wrap;
    flex-grow: 0;
    text-align: left;
    margin: 15px auto;
    max-width: 300px;

    dt {
      padding: 4px 0;
      text-align: right;
      width: 18%;
      line-height: 1.2rem;
      span {
        display: inline-block;
        margin: 3px 0 0 0;
        height: 8px;
        width: 8px;
        border-radius: 30px;
      }
    }
    dd {
      width: 82%;
      line-height: 1.2rem;
      padding: 5px 10px;
    }
  }

  &__fertileWindows {
    padding: 0 25px;
    p {
      display: inline-block;
      width: 100%;
      text-align: left;
      margin: 0;
    }
  }

  &__note {
    margin: 15px 25px;
    display: block;
    text-align: left;
    padding: 15px;
    background-color: #f5f5f5;
  }

  &__cta {
    display: inline-block;
    padding: 18px;
    a {
      color: white !important;
      text-decoration: none;
      padding: 18px;
      transition: all;
      transition-duration: .15s;
      transition-timing-function: ease-in-out;
      &:hover {
        opacity: 0.9;
        svg {
          transform: translate3d(5px,0,0);
        }
      }
      svg {
        transition: all;
        transition-duration: .15s;
        transition-timing-function: ease-in-out;
        margin: -0.5rem -0.5rem -0.9rem 0.5rem;
        fill: #fff;
        height: 2.5rem;
        width: 2.5rem;
      }
    }
  }

  .v-picker--date {
    .accent {
      background-color: #aaa !important;
    }
  }
}
</style>
